import React, { useState, useEffect } from 'react';
import { promoAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tag, Loader2, CheckCircle, X, Percent, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const PromoCodeInput = ({ rideAmount, onPromoApplied, rideId }) => {
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [availablePromos, setAvailablePromos] = useState([]);
  const [showAvailable, setShowAvailable] = useState(false);

  useEffect(() => {
    loadAvailablePromos();
  }, []);

  const loadAvailablePromos = async () => {
    try {
      const response = await promoAPI.getAvailable();
      setAvailablePromos(response.data);
    } catch (error) {
      console.error('Error loading promos:', error);
    }
  };

  const validateCode = async () => {
    if (!code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setValidating(true);
    try {
      const response = await promoAPI.validate(code.trim(), rideAmount);
      setPromoResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setPromoResult(null);
    } finally {
      setValidating(false);
    }
  };

  const applyCode = async () => {
    if (!rideId || !promoResult) return;

    setApplying(true);
    try {
      const response = await promoAPI.apply(rideId, code.trim());
      toast.success(`Promo applied! You saved $${response.data.discount.toFixed(2)}`);
      if (onPromoApplied) {
        onPromoApplied(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply promo code');
    } finally {
      setApplying(false);
    }
  };

  const selectPromo = (promoCode) => {
    setCode(promoCode);
    setShowAvailable(false);
    setPromoResult(null);
  };

  const clearPromo = () => {
    setCode('');
    setPromoResult(null);
  };

  return (
    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-5 h-5 text-red-600" />
          <span className="font-semibold text-slate-900 dark:text-white">Promo Code</span>
        </div>

        {/* Input Section */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setPromoResult(null);
              }}
              placeholder="Enter code"
              className="pr-8 uppercase"
              data-testid="promo-code-input"
            />
            {code && (
              <button
                onClick={clearPromo}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={validateCode}
            disabled={validating || !code.trim()}
            variant="outline"
            className="flex-shrink-0"
            data-testid="validate-promo-btn"
          >
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
          </Button>
        </div>

        {/* Available Promos Toggle */}
        {availablePromos.length > 0 && (
          <button
            onClick={() => setShowAvailable(!showAvailable)}
            className="text-sm text-red-600 hover:text-red-700 mb-3"
          >
            {showAvailable ? 'Hide available codes' : `View ${availablePromos.length} available codes`}
          </button>
        )}

        {/* Available Promos List */}
        {showAvailable && (
          <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
            {availablePromos.map((promo) => (
              <button
                key={promo.id}
                onClick={() => selectPromo(promo.code)}
                className="w-full p-2 text-left bg-slate-50 dark:bg-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-red-600">{promo.code}</span>
                  <span className="text-sm text-slate-500">
                    {promo.discount_type === 'percentage' ? (
                      <span className="flex items-center gap-1">
                        <Percent className="w-3 h-3" />
                        {promo.discount_value}% off
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        ${promo.discount_value} off
                      </span>
                    )}
                  </span>
                </div>
                {promo.description && (
                  <p className="text-xs text-slate-400 mt-1">{promo.description}</p>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Validation Result */}
        {promoResult && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-700 dark:text-green-400">
                Valid code!
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Original</span>
                <span className="text-slate-900 dark:text-white">${rideAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-${promoResult.calculated_discount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold border-t border-green-200 dark:border-green-800 pt-1 mt-1">
                <span className="text-slate-900 dark:text-white">New Total</span>
                <span className="text-green-600">${promoResult.final_amount.toFixed(2)}</span>
              </div>
            </div>

            {rideId && (
              <Button
                onClick={applyCode}
                disabled={applying}
                className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-promo-btn"
              >
                {applying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Confirm & Apply'
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PromoCodeInput;
